"use client";

import { FolderSearch } from "lucide-react";
import { Dispatch, SetStateAction } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from "@/components/ui/table";
import {
  ColumnDef,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

interface DataTableProps<TData> {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  pageCount?: number;
  isLoading?: boolean;
  sorting?: SortingState;
  onSortingChange?: Dispatch<SetStateAction<SortingState>>;
}

export function DataTable<TData>({
  columns,
  data,
  pagination,
  onPaginationChange,
  pageCount,
  isLoading,
  sorting,
  onSortingChange,
}: DataTableProps<TData>) {
  const { t } = useTranslation();
  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
      },
    },
    state: {
      pagination,
      ...(sorting !== undefined && { sorting }),
    },
    onSortingChange: onSortingChange ?? undefined,
    defaultColumn: {
      size: 999,
    },
    manualPagination: true,
    manualSorting: true,
    pageCount: pageCount,
    onPaginationChange: onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Table className="rounded-md border border-border">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow className="hover:bg-transparent" key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize() === 999 ? "auto" : header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              <p className="mb-2 flex flex-col items-center justify-center">
                <FolderSearch className="size-6 text-foreground" />
              </p>
              <p className="font-semibold text-foreground">{t("empty.noResults")}</p>
              <p className="text-foreground">{t("empty.tryDifferentSearch")}</p>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TablePagination table={table} isLoading={isLoading} />
      </TableFooter>
    </Table>
  );
}
